import type { Todo } from "../../domain/entities.ts";
import type { MyDayPlan } from "./my-day.ts";
import { myDaySuggestions } from "./my-day.ts";
import { escapeHtml } from "../../ui/text.ts";

export function myDayPlanningReasons(task: Todo, carryIds: string[], date: string, userId: string): string[] {
  return [
    carryIds.includes(task.id) ? "unfinished from your previous plan" : "",
    task.dueDate && task.dueDate < date ? "overdue" : "",
    task.dueDate === date ? "due today" : task.startDate === date ? "starts today" : "",
    task.priority === "urgent" ? "urgent" : task.priority === "high" ? "high priority" : "",
    (task.assignedTo || []).includes(userId) ? "assigned to you" : "",
  ].filter((reason): reason is string => Boolean(reason));
}

export interface MyDayPickerOptions {
  plan: MyDayPlan;
  tasks: Todo[];
  carryIds: string[];
  userId: string;
  query: string;
  limit: number;
  unfinishedOpen: boolean;
  context(task: Todo): string;
  icon(name: string): string;
}

export function myDayPickerResultsView(options: MyDayPickerOptions): string {
  const { plan, tasks, carryIds, userId, context, icon } = options;
  const query = options.query.trim().toLocaleLowerCase();
  const available = tasks.filter(task => !task.deleted && !task.archivedAt && !task.completed);
  const selected = new Set(plan.taskIds);
  const row = (task: Todo): string => {
    const added = selected.has(task.id);
    const detail = [context(task), ...myDayPlanningReasons(task, carryIds, plan.date, userId)].filter(Boolean).join(" · ");
    return `<li class="my-day-picker__row"><div><strong>${escapeHtml(task.title || "Untitled task")}</strong>${detail ? `<small>${escapeHtml(detail)}</small>` : ""}</div><button type="button" class="button button--soft ${added ? "is-added" : ""}" data-my-day-toggle="${escapeHtml(task.id)}" aria-pressed="${added}" aria-label="${added ? "Remove" : "Add"} ${escapeHtml(task.title || "Untitled task")} ${added ? "from" : "to"} My Day">${icon(added ? "check" : "plus")}<span>${added ? "Added" : "Add"}</span></button></li>`;
  };
  const list = (items: Todo[]) => `<ul class="my-day-picker__list">${items.map(row).join("")}</ul>`;
  if (query) {
    const matches = available.filter(task => `${task.title} ${context(task)}`.toLocaleLowerCase().includes(query));
    return `<p class="my-day-picker__hint" role="status">${matches.length} matching ${matches.length === 1 ? "task" : "tasks"}</p>${list(matches.slice(0, options.limit))}${matches.length > options.limit ? `<button type="button" class="text-button" data-my-day-more>Show more results</button>` : ""}`;
  }
  // Keep chosen suggestions visible so adding one does not shift the next row under the pointer.
  const suggested = myDaySuggestions({ ...plan, taskIds: [] }, available, userId, plan.date, carryIds);
  const carried = suggested.filter(task => carryIds.includes(task.id));
  const other = suggested.filter(task => !carryIds.includes(task.id));
  return `${carried.length ? `<details class="my-day-picker__unfinished" data-my-day-unfinished ${options.unfinishedOpen ? "open" : ""}><summary>Unfinished from your previous plan <span>${carried.length}</span></summary><p class="my-day-picker__hint">Choose what still matters today. Nothing is added automatically.</p>${list(carried.slice(0, options.limit))}${carried.length > options.limit ? `<button type="button" class="text-button" data-my-day-more>Show more unfinished tasks</button>` : ""}${carried.some(task => !selected.has(task.id)) ? `<button type="button" class="text-button" data-my-day-add-all>Add all unfinished to My Day</button>` : ""}</details>` : ""}<h3>Suggested for today</h3>${other.length ? list(other.slice(0, options.limit)) : `<p class="my-day-picker__hint">No suggestions right now. Search for an existing task to add.</p>`}${other.length > options.limit ? `<button type="button" class="text-button" data-my-day-more>Show more suggestions</button>` : ""}`;
}

export function myDayPickerView(options: MyDayPickerOptions): string {
  return `<section class="my-day-picker" data-my-day-picker aria-labelledby="my-day-picker-title"><header><h2 id="my-day-picker-title">Choose tasks for today</h2><button type="button" class="button button--soft" data-my-day-close>Done choosing</button></header><p class="my-day-picker__hint">Choose a few tasks to focus on. Adding them here doesn't change their dates, assignments, or collections.</p><label class="my-day-picker__search" for="my-day-search">Find a task<input id="my-day-search" type="search" maxlength="200" placeholder="Search tasks across your spaces…" value="${escapeHtml(options.query)}" autocomplete="off"></label><p class="my-day-picker__error" role="alert" hidden></p><div data-my-day-results>${myDayPickerResultsView(options)}</div></section>`;
}

export interface MyDayPickerCapabilities {
  options(): MyDayPickerOptions;
  search(query: string): void;
  more(): void;
  unfinished(open: boolean): void;
  toggle(id: string): Promise<void>;
  addAll(): Promise<void>;
  close(): Promise<void>;
}

export function bindMyDayPicker(root: ParentNode, capabilities: MyDayPickerCapabilities): void {
  const picker = root.querySelector<HTMLElement>("[data-my-day-picker]");
  if (!picker) return;
  let busy = false;
  const updateResults = () => {
    picker.querySelector<HTMLElement>("[data-my-day-results]")!.innerHTML = myDayPickerResultsView(capabilities.options());
  };
  picker.querySelector<HTMLInputElement>("#my-day-search")?.addEventListener("input", event => {
    capabilities.search((event.target as HTMLInputElement).value);
    updateResults();
  });
  picker.addEventListener("toggle", event => {
    const details = event.target as HTMLDetailsElement;
    if (details.matches("[data-my-day-unfinished]")) capabilities.unfinished(details.open);
  }, true);
  picker.addEventListener("keydown", event => {
    if (!event.metaKey && !event.ctrlKey && !event.altKey && ["ArrowDown", "ArrowUp", "j", "k", "Home", "End"].includes(event.key)) event.stopPropagation();
    if (event.key === "Escape") {
      event.preventDefault(); event.stopPropagation();
      if (!busy) void capabilities.close();
    }
  });
  picker.addEventListener("click", async event => {
    const button = (event.target as Element).closest<HTMLButtonElement>("button");
    if (!button || busy) return;
    if (button.hasAttribute("data-my-day-more")) { capabilities.more(); updateResults(); return; }
    busy = true;
    picker.setAttribute("aria-busy", "true");
    picker.querySelectorAll<HTMLButtonElement>("button").forEach(item => { item.disabled = true; });
    const error = picker.querySelector<HTMLElement>(".my-day-picker__error")!;
    error.hidden = true;
    try {
      if (button.hasAttribute("data-my-day-close")) await capabilities.close();
      else if (button.hasAttribute("data-my-day-add-all")) await capabilities.addAll();
      else if (button.dataset.myDayToggle) await capabilities.toggle(button.dataset.myDayToggle);
    } catch {
      error.textContent = "Could not save your My Day selection on this device. Please try again.";
      error.hidden = false;
    } finally {
      busy = false;
      picker.removeAttribute("aria-busy");
      picker.querySelectorAll<HTMLButtonElement>("button").forEach(item => { item.disabled = false; });
    }
  });
}
